import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AiExampleRef, AiGenerateRequest, AiImage, AiMode, MediaSummary } from "@media-viewer/shared";
import { api, ApiError } from "../api/client";
import { ModelPicker } from "../components/ai/ModelPicker";
import { ExamplePicker } from "../components/ai/ExamplePicker";
import { FailedCard, PendingCard, ResultCard } from "../components/ai/ResultCard";

interface Example {
  key: string;
  ref: AiExampleRef;
  thumb: string;
  device: boolean;
}

const MAX_EXAMPLES = 10;
const MAX_LONG_SIDE = 4096;
const MAX_DATA_URI = 30_000_000;

async function fileToDataUri(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_LONG_SIDE / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bmp.width * scale));
  canvas.height = Math.max(1, Math.round(bmp.height * scale));
  canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();
  if (file.type === "image/png") {
    const png = canvas.toDataURL("image/png");
    if (png.length <= MAX_DATA_URI) return png;
  }
  return canvas.toDataURL("image/jpeg", 0.92);
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

const sectionLabel = "text-xs uppercase tracking-wider text-zinc-400";

function Chips<T extends string | number>({
  label,
  values,
  value,
  onChange,
  allowDefault,
}: {
  label: string;
  values: T[];
  value: T | undefined;
  onChange: (v: T | undefined) => void;
  allowDefault: boolean;
}) {
  if (values.length === 0) return null;
  const options: (T | undefined)[] = allowDefault ? [undefined, ...values] : values;
  return (
    <div className="flex flex-col gap-2">
      <span className={sectionLabel}>{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((v) => (
          <button
            key={String(v)}
            aria-pressed={value === v}
            onClick={() => onChange(v)}
            className={
              "rounded px-3 py-1.5 text-sm " +
              (value === v
                ? "bg-zinc-600 text-zinc-50"
                : "border border-zinc-700 bg-zinc-950 text-zinc-300 hover:border-zinc-500")
            }
          >
            {v === undefined ? "Default" : String(v)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Generate() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<AiMode>("generate");
  const [modelIds, setModelIds] = useState<Record<AiMode, string | null>>({ generate: null, edit: null });
  const [examples, setExamples] = useState<Example[]>([]);
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [seed, setSeed] = useState("");
  const [aspectRatio, setAspectRatio] = useState<string | undefined>(undefined);
  const [resolution, setResolution] = useState<string | undefined>(undefined);
  const [quality, setQuality] = useState<string | undefined>(undefined);
  const [variants, setVariants] = useState(1);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [examplePickerOpen, setExamplePickerOpen] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [discarded, setDiscarded] = useState<Set<number>>(new Set());
  const fileInput = useRef<HTMLInputElement>(null);

  const status = useQuery({ queryKey: ["ai", "status"], queryFn: api.ai.status });
  const modelsQ = useQuery({ queryKey: ["ai", "models"], queryFn: api.ai.models, staleTime: 5 * 60_000 });
  const models = modelsQ.data?.models ?? [];

  useEffect(() => {
    if (!modelsQ.data || modelIds[mode]) return;
    const forMode = modelsQ.data.models.filter((m) => m.mode === mode);
    const pick = forMode.find((m) => m.traits.includes("default")) ?? forMode[0];
    if (pick) setModelIds((cur) => ({ ...cur, [mode]: pick.id }));
  }, [modelsQ.data, mode, modelIds]);

  const model = models.find((m) => m.id === modelIds[mode] && m.mode === mode) ?? null;

  useEffect(() => {
    if (!model) return;
    setAspectRatio((v) =>
      v !== undefined && model.aspectRatios.includes(v) ? v : model.defaultAspectRatio,
    );
    setResolution((v) => (v !== undefined && model.resolutions.includes(v) ? v : undefined));
    setQuality((v) => (v !== undefined && model.qualities.includes(v) ? v : undefined));
  }, [model]);

  const isEdit = mode === "edit";
  const cap = isEdit && model ? model.maxInputImages : MAX_EXAMPLES;
  const room = Math.max(0, cap - examples.length);
  const tooManyExamples = isEdit && model !== null && examples.length > model.maxInputImages;

  const priceBody = useMemo(
    () =>
      model
        ? {
            mode,
            modelId: model.id,
            aspectRatio,
            resolution,
            quality,
            variants: isEdit ? 1 : variants,
            exampleCount: isEdit ? examples.length : 0,
          }
        : null,
    [model, mode, aspectRatio, resolution, quality, variants, isEdit, examples.length],
  );
  const debouncedPriceBody = useDebounced(priceBody, 300);
  const price = useQuery({
    queryKey: ["ai", "price", debouncedPriceBody],
    queryFn: () => api.ai.price(debouncedPriceBody!),
    enabled: debouncedPriceBody !== null && !tooManyExamples,
  });
  const credits = priceBody === debouncedPriceBody ? price.data?.credits : undefined;

  const st = status.data;
  const remaining = st && !st.unlimited ? (st.remaining ?? 0) : null;
  const overBudget = credits !== undefined && remaining !== null && credits > remaining;

  const generations = useQuery({
    queryKey: ["ai", "generations"],
    queryFn: api.ai.list,
    refetchInterval: (q) => (q.state.data?.generations.some((g) => g.status === "pending") ? 2000 : false),
  });
  const pendingCount = generations.data?.generations.filter((g) => g.status === "pending").length ?? 0;
  const prevPending = useRef(pendingCount);
  useEffect(() => {
    if (pendingCount < prevPending.current) void qc.invalidateQueries({ queryKey: ["ai", "status"] });
    prevPending.current = pendingCount;
  }, [pendingCount, qc]);

  const generate = useMutation({
    mutationFn: (body: AiGenerateRequest) => api.ai.generate(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai", "generations"] });
      void qc.invalidateQueries({ queryKey: ["ai", "status"] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ["ai", "status"] });
    },
  });

  function generateErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      const body = err.body as { error?: string; remaining?: number } | null;
      if (err.status === 402)
        return `Not enough credits left this month${body?.remaining !== undefined ? ` (${body.remaining} left)` : ""}`;
      if (err.status === 503) return "Venice is unavailable right now — try again later";
      if (err.status === 429) return "Slow down — try again in a minute";
      if (err.status === 413) return "The example images are too large to send.";
      if (err.status === 400) {
        const known: Record<string, string> = {
          bad_example: "One of the example images can't be used.",
          too_many_examples: "Too many example images for this model.",
          bad_option: "That option isn't available for this model.",
          prompt_too_long: "The prompt is too long for this model.",
        };
        if (body?.error && known[body.error]) return known[body.error]!;
      }
      if (err.status === 400) return `Venice couldn't take this request (${body?.error ?? "bad request"})`;
    }
    return "Couldn't start the generation — try again";
  }

  let blocker: string | null = null;
  if (!model) blocker = modelsQ.isError ? null : "Choose a model";
  else if (isEdit && examples.length === 0) blocker = "Add at least one example image";
  else if (tooManyExamples)
    blocker = `This model takes up to ${plural(model.maxInputImages, "image")} — remove ${plural(examples.length - model.maxInputImages, "example image")}`;
  else if (prompt.trim() === "") blocker = "Write a prompt";
  else if (overBudget) blocker = "Not enough credits left this month";

  const canGenerate = model !== null && blocker === null && credits !== undefined && !generate.isPending;

  function submit() {
    if (!model || !canGenerate) return;
    const seedNum = seed.trim() === "" ? undefined : Number(seed);
    generate.mutate({
      mode,
      modelId: model.id,
      prompt: prompt.trim(),
      negativePrompt: negativePrompt.trim() || undefined,
      aspectRatio,
      resolution,
      quality,
      variants: isEdit ? 1 : variants,
      seed: seedNum !== undefined && Number.isInteger(seedNum) ? seedNum : undefined,
      examples: isEdit ? examples.map((e) => e.ref) : [],
    });
  }

  function addFromCollection(items: MediaSummary[]) {
    setExamples((cur) => {
      const have = new Set(cur.map((e) => e.key));
      const add = items
        .filter((i) => !have.has(`m${i.id}`))
        .map((i) => ({ key: `m${i.id}`, ref: { mediaId: i.id }, thumb: i.thumbUrl, device: false }));
      return [...cur, ...add];
    });
  }

  async function addFromDevice(files: FileList | null) {
    if (!files || files.length === 0) return;
    setDeviceError(null);
    const list = Array.from(files);
    const take = list.slice(0, room);
    const added: Example[] = [];
    const failed: string[] = [];
    for (const f of take) {
      try {
        const dataUri = await fileToDataUri(f);
        added.push({ key: `d${Date.now()}-${Math.random()}`, ref: { dataUri }, thumb: dataUri, device: true });
      } catch {
        failed.push(f.name);
      }
    }
    setExamples((cur) => [...cur, ...added]);
    const msgs: string[] = [];
    if (failed.length) msgs.push(`Couldn't read ${failed.join(", ")}`);
    if (list.length > take.length) msgs.push(`Only ${plural(take.length, "image")} fit with this model`);
    if (msgs.length) setDeviceError(msgs.join(". "));
  }

  function addGeneratedExample(image: AiImage) {
    const key = `g${image.id}`;
    setExamples((cur) =>
      cur.some((e) => e.key === key)
        ? cur
        : [...cur, { key, ref: { generatedImageId: image.id }, thumb: image.url, device: false }],
    );
    setMode("edit");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const resetLabel = st ? new Date(st.resetsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

  const gens = generations.data?.generations ?? [];
  const cards = gens.flatMap((g) => {
    if (g.status === "pending") return [<PendingCard key={`p${g.id}`} g={g} />];
    if (g.status === "failed") return [<FailedCard key={`f${g.id}`} g={g} />];
    return g.images
      .filter((i) => !discarded.has(i.id))
      .map((i) => (
        <ResultCard
          key={`i${i.id}`}
          g={g}
          image={i}
          onDiscarded={(id) => setDiscarded((s) => new Set(s).add(id))}
          onUseAsExample={addGeneratedExample}
        />
      ));
  });

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-6 p-4 sm:p-6 lg:flex-row lg:items-start">
      <section
        aria-label="Generation settings"
        className="flex w-full flex-col gap-5 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5 lg:w-[420px] lg:shrink-0"
      >
        <h1 className="text-xl font-semibold">Generate an image</h1>

        <div role="tablist" aria-label="Mode" className="grid grid-cols-2 gap-1 rounded-md border border-zinc-800 bg-zinc-950 p-1">
          {(
            [
              ["generate", "From a prompt"],
              ["edit", "From example images"],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={
                "min-h-[40px] rounded text-sm " +
                (mode === m ? "bg-zinc-700 text-zinc-50" : "text-zinc-400 hover:text-zinc-200")
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <span className={sectionLabel}>Model</span>
          <button
            onClick={() => setModelPickerOpen(true)}
            disabled={!modelsQ.data}
            className="flex min-h-[56px] items-center justify-between gap-3 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-left hover:border-zinc-500 disabled:opacity-60"
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-semibold">
                {model ? model.name : modelsQ.isError ? "Couldn't load models from Venice" : "Loading models…"}
              </span>
              {model && (
                <span className="text-xs text-zinc-400">
                  {isEdit && `Up to ${plural(model.maxInputImages, "example image")} · `}
                  from {plural(model.priceHint.minCredits, "credit")}
                </span>
              )}
            </span>
            <span className="shrink-0 text-sm text-sky-300">Change</span>
          </button>
        </div>

        {isEdit && (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className={sectionLabel}>Example images</span>
              <span className={"text-xs " + (tooManyExamples ? "text-amber-300" : "text-zinc-500")}>
                {examples.length} of {cap}
              </span>
            </div>
            {examples.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {examples.map((e, i) => (
                  <div key={e.key} className="relative h-20 w-20 overflow-hidden rounded border border-zinc-700 bg-zinc-800">
                    <img src={e.thumb} alt="" className="h-full w-full object-cover" />
                    <span className="absolute bottom-0 left-0 rounded-tr bg-black/70 px-1.5 py-0.5 text-[10px] text-zinc-100">
                      {i + 1}
                      {i === 0 ? " · base" : e.device ? " · device" : ""}
                    </span>
                    <button
                      aria-label={`Remove example image ${i + 1}`}
                      onClick={() => setExamples((cur) => cur.filter((x) => x.key !== e.key))}
                      className="absolute right-0.5 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-zinc-100 hover:bg-black"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setExamplePickerOpen(true)}
                disabled={room === 0}
                className="rounded bg-zinc-800 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
              >
                Add from collection
              </button>
              <button
                onClick={() => fileInput.current?.click()}
                disabled={room === 0}
                className="rounded bg-zinc-800 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
              >
                Upload from device
              </button>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  void addFromDevice(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {deviceError && <span className="text-xs text-rose-400">{deviceError}</span>}
            <span className="text-xs text-zinc-500">
              The first image is the base; the others are used as references. Device uploads are only used for this
              generation and aren't added to the collection.
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <label htmlFor="prompt" className={sectionLabel}>
              Prompt
            </label>
            {model?.promptCharacterLimit && (
              <span className="text-xs text-zinc-500">
                {prompt.length} / {model.promptCharacterLimit}
              </span>
            )}
          </div>
          <textarea
            id="prompt"
            rows={4}
            value={prompt}
            maxLength={model?.promptCharacterLimit}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={isEdit ? "Describe how to change or combine the example images" : "Describe the image"}
            className="w-full resize-y rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
          />
        </div>

        {model && (
          <div className="flex flex-col gap-4">
            <Chips
              label="Aspect ratio"
              values={model.aspectRatios}
              value={aspectRatio}
              onChange={setAspectRatio}
              allowDefault={model.defaultAspectRatio === undefined}
            />
            <Chips label="Resolution" values={model.resolutions} value={resolution} onChange={setResolution} allowDefault />
            <Chips label="Quality" values={model.qualities} value={quality} onChange={setQuality} allowDefault />
            {!isEdit && (
              <Chips
                label="Variants"
                values={[1, 2, 3, 4]}
                value={variants}
                onChange={(v) => setVariants(v ?? 1)}
                allowDefault={false}
              />
            )}
            <details className="group">
              <summary className="cursor-pointer text-sm text-zinc-400 hover:text-zinc-200">More options</summary>
              <div className="mt-3 flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className={sectionLabel}>Negative prompt</span>
                  <textarea
                    rows={2}
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder="What to avoid"
                    className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={sectionLabel}>Seed</span>
                  <input
                    type="number"
                    step={1}
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder="Random"
                    className="w-40 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-100"
                  />
                </label>
              </div>
            </details>
          </div>
        )}

        <div className="flex flex-col gap-1.5 rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-zinc-400">This generation</span>
            <span className="font-semibold tabular-nums">
              {credits !== undefined ? plural(credits, "credit") : price.isError ? "—" : "…"}
            </span>
          </div>
          <div className="flex justify-between gap-2 text-zinc-400">
            <span>Left after this</span>
            <span className={"text-right " + (overBudget ? "text-rose-400" : "")}>
              {!st
                ? "…"
                : st.unlimited
                  ? "Unlimited"
                  : `${credits !== undefined ? Math.max(0, (st.remaining ?? 0) - credits) : (st.remaining ?? 0)} of ${st.allowance} · resets ${resetLabel}`}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={submit}
            disabled={!canGenerate}
            className="min-h-[44px] rounded-md bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400"
          >
            {generate.isPending
              ? "Starting…"
              : credits !== undefined
                ? `Generate · ${plural(credits, "credit")}`
                : "Generate"}
          </button>
          {blocker && <span className={"text-xs " + (overBudget ? "text-rose-400" : "text-zinc-500")}>{blocker}</span>}
          {generate.isError && <span className="text-xs text-rose-400">{generateErrorMessage(generate.error)}</span>}
        </div>
      </section>

      <section aria-label="Results" className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Results</h2>
          <span className="text-sm text-zinc-400">
            Not in the collection until you save. Unsaved results are cleared after 24 hours.
          </span>
        </div>
        {generations.isPending ? (
          <p className="text-zinc-500">Loading…</p>
        ) : cards.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-800 p-8 text-center text-zinc-500">
            Nothing yet — your generated images will show up here.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{cards}</div>
        )}
      </section>

      <ModelPicker
        open={modelPickerOpen}
        onClose={() => setModelPickerOpen(false)}
        models={models}
        mode={mode}
        exampleCount={examples.length}
        selectedId={model?.id ?? null}
        onSelect={(id) => setModelIds((cur) => ({ ...cur, [mode]: id }))}
      />
      <ExamplePicker
        open={examplePickerOpen}
        onClose={() => setExamplePickerOpen(false)}
        room={room}
        onAdd={addFromCollection}
      />
    </div>
  );
}
